import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ClubBook } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Spinner } from "../../components/ui";
import { BookSearch, type SearchNote } from "../../components/BookSearch";
import { ChevronIcon, PlusIcon } from "../../components/Icons";
import { NoteEditor, SuggestionRow, useSuggestionRefresh } from "../../components/Suggestion";
import { useClub } from "./ClubLayout";
import { BackBar } from "../../components/ui";
import { BookPanel } from "./ShelfTab";
import { useClubBooks, useMe } from "../../lib/queries";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.suggestions;

/*
 * The Suggestions page (PLAN.md §5b): where the club reviews what it might
 * read. Everyone's: New this week first, so a fresh suggestion gets seen
 * before it has taps; then Most wanted, picking up after the books on deck,
 * with a line where the kept top ends; then Past. Mine: your suggestions with
 * their place and any warning, and yours in Past with the one bump.
 */
export function SuggestionsPage() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const navigate = useNavigate();
  const { book: openId = null, mine: mineView = false, add = false } = useSearch({ from: "/authed/clubs/$clubId/suggestions" });
  const me = useMe();
  const q = useQuery({ queryKey: ["suggestions", clubId], queryFn: () => api.suggestions(clubId) });
  const books = useClubBooks(clubId);
  const refresh = useSuggestionRefresh(clubId);
  const [text, setText] = useState("");
  const [added, setAdded] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<ClubBook | null>(null);
  const setSearch = (s: { book?: string; mine?: boolean; add?: boolean }) =>
    void navigate({ to: "/clubs/$clubId/suggestions", params: { clubId }, search: { ...(s.book ? { book: s.book } : {}), ...(s.mine ? { mine: true } : {}), ...(s.add ? { add: true } : {}) }, replace: true });
  const addBook = useMutation({
    mutationFn: (hc: number) => api.addBook(clubId, hc),
    onSuccess: async (b) => { await refresh(); setAdded(b.duplicate ? txt.addedYourInterestIn(b.book.title) : null); setJustAdded(b.duplicate ? null : b); setSearch({ mine: mineView }); },
  });
  const isAdmin = c.myRole !== "member";
  const canAdd = isAdmin || c.settings.onDeckPolicy === "everyone";
  const meId = me.data?.id;
  const turn = books.data?.turn ?? null;
  const canStart = isAdmin || (!!turn && turn.user.id === meId);

  const all = useMemo(() => q.data ? [...q.data.running, ...q.data.past] : [], [q.data]);
  if (q.isPending) return <main className="page"><Spinner /></main>;
  if (q.isError) return <main className="page"><p className="error">{describeError(q.error)}</p></main>;
  const d = q.data;
  const open = openId ? all.find((b) => b.id === openId) ?? null : null;
  const t = text.trim().toLowerCase();
  const matches = (b: ClubBook) => !t || b.book.title.toLowerCase().includes(t) || b.book.authors.some((a) => a.toLowerCase().includes(t));
  const beyondDeck = d.running.filter((b) => !b.suggestion?.onDeck);
  const fresh = beyondDeck.filter((b) => b.suggestion?.isNew);
  const wanted = beyondDeck.filter((b) => !b.suggestion?.isNew);
  const mineRunning = d.running.filter((b) => b.addedBy?.id === meId);
  const minePast = d.past.filter((b) => b.addedBy?.id === meId);
  const current = books.data?.current ?? null;
  // What the search knows: already in the running, in Past, being read now, or read before.
  const noteFor = (hc: number): SearchNote | null => {
    if (current?.book.hardcoverId === hc) return { disabled: true, action: txt.readingNow };
    const r = d.running.find((b) => b.book.hardcoverId === hc);
    if (r) return { action: r.suggestion?.mine ? txt.wanted : txt.wantToRead, disabled: r.suggestion?.mine, note: txt.already(r.suggestion?.onDeck ? txt.onDeck : txt.inTheRunning, r.addedBy ? txt.suggestedBy(r.addedBy.id === meId ? txt.you : r.addedBy.name) : "") };
    const p = d.past.find((b) => b.book.hardcoverId === hc);
    if (p) return { action: txt.bringItBack, note: txt.inPastSuggestedBy(p.addedBy?.id === meId ? txt.you : p.addedBy?.name ?? txt.someone) };
    const read = books.data?.finished.find((b) => b.book.hardcoverId === hc);
    if (read?.finishedAt) return { note: txt.theClubReadThis(new Date(read.finishedAt).getFullYear()) };
    return null;
  };
  const row = (b: ClubBook, showRank = false) => (
    <SuggestionRow key={b.id} clubId={clubId} b={b} meId={meId} isAdmin={isAdmin} canStart={canStart} showRank={showRank}
      onOpen={() => setSearch({ book: b.id, mine: mineView })}
      onStart={() => setSearch({ book: b.id, mine: mineView })} /* starting asks first: it happens in the book's pane */ />
  );
  const keep = d.keep;
  // Most wanted, with a quiet line where the kept top ends (only when Past is on).
  const wantedRows: React.ReactNode[] = [];
  wanted.filter(matches).forEach((b, i, arr) => {
    wantedRows.push(row(b));
    const next = arr[i + 1];
    if (keep !== null && b.suggestion?.rank !== null && b.suggestion!.rank! <= keep && next && (next.suggestion?.rank ?? 0) > keep) {
      wantedRows.push(<div key="cut" className="cut">{txt.keptInTheRunning(keep)}</div>);
    }
  });

  return (
    <main className={`page stack suggestions${open ? " with-panel" : ""}`}>
      <BackBar label={txt.shelf} link={{ to: "/clubs/$clubId/shelf", params: { clubId } }}
        right={canAdd && !add ? <button type="button" className="btn primary sm" onClick={() => { setAdded(null); setSearch({ add: true, mine: mineView }); }}><PlusIcon width={16} height={16} />{txt.addABook}</button> : undefined} />
      <div className="row spread" style={{ flexWrap: "wrap", gap: 10 }}>
        <h1>{txt.suggestions}</h1>
        <div className="seg" role="radiogroup" aria-label={txt.whoseSuggestions}>
          <button type="button" aria-pressed={!mineView} onClick={() => setSearch({})}>{txt.everyoneS}</button>
          <button type="button" aria-pressed={mineView} onClick={() => setSearch({ mine: true })}>{txt.mine}</button>
        </div>
      </div>

      {add && (
        <div className="card stack-sm">
          <div className="row spread"><h3>{txt.addABook}</h3><button className="btn ghost sm" onClick={() => setSearch({ mine: mineView })}>{txt.done}</button></div>
          {d.mine.cap !== null && <p className="muted small" style={{ margin: 0 }}>{txt.youHaveOfSuggestions(d.mine.active, d.mine.cap)}</p>}
          <BookSearch onPick={(b) => addBook.mutate(b.hardcoverId)} picking={addBook.isPending ? addBook.variables : null} noteFor={noteFor} />
          {addBook.isError && <p className="error small">{describeError(addBook.error)}{addBook.error && describeError(addBook.error).includes("Move one") && <> <button type="button" className="linkish" onClick={() => setSearch({ mine: true })}>{txt.seeYours}</button></>}</p>}
        </div>
      )}
      {added && !add && <p className="ok small" style={{ margin: 0 }}>{added}</p>}
      {justAdded && !add && (
        <div className="card stack-sm">
          <div className="small"><b>{txt.suggested(justAdded.book.title)}</b></div>
          <NoteEditor clubId={clubId} b={justAdded} prompt={txt.tellTheClubWhy} onDone={() => setJustAdded(null)} />
        </div>
      )}

      <input id="sugg-search" className="input" type="search" placeholder={txt.searchSuggestionsAndPast} value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />

      {!mineView ? (
        <>
          {fresh.filter(matches).length > 0 && (
            <section className="stack-sm">
              <div className="sect"><h3>{txt.newThisWeek}</h3><span className="count">{fresh.length}</span></div>
              <div className="deck">{fresh.filter(matches).map((b) => row(b))}</div>
            </section>
          )}
          <section className="stack-sm">
            <div className="sect"><h3>{txt.mostWanted}</h3><span className="count">{d.running.some((b) => b.suggestion?.onDeck) ? txt.afterTheOnDeck(d.running.filter((b) => b.suggestion?.onDeck).length) : txt.inTheRunning}</span></div>
            {wantedRows.length > 0 ? <div className="deck">{wantedRows}</div>
              : <p className="muted small">{t ? txt.nothingInTheRunning : d.running.length === 0 ? txt.nothingSuggestedYetAdd : txt.everythingInTheRunning}</p>}
          </section>
          {d.past.length > 0 && (
            <details className="fold past" open={Boolean(t)}>
              <summary><span className="sect"><h3>{txt.pastSuggestions}</h3><span className="count">{d.past.length}{keep !== null ? txt.quietForDays(c.settings.retireDays) : ""}</span></span><ChevronIcon className="chev" width={16} height={16} aria-hidden="true" /></summary>
              <div className="deck" style={{ marginTop: 8 }}>{d.past.filter(matches).map((b) => row(b))}</div>
            </details>
          )}
        </>
      ) : (
        <>
          <section className="stack-sm">
            <div className="sect"><h3>{txt.yours}</h3><span className="count">{d.mine.cap !== null ? txt.ofGoing(d.mine.active, d.mine.cap) : `${mineRunning.length}`}</span></div>
            {mineRunning.filter(matches).length > 0 ? <div className="deck">{mineRunning.filter(matches).map((b) => row(b, true))}</div>
              : <p className="muted small">{txt.youHavenTSuggested}</p>}
          </section>
          {minePast.length > 0 && (
            <section className="stack-sm">
              <div className="sect"><h3>{txt.yoursInPast}</h3><span className="count">{minePast.length}</span></div>
              <div className="deck">{minePast.filter(matches).map((b) => row(b))}</div>
            </section>
          )}
        </>
      )}

      {open && <BookPanel key={open.id} b={open} clubId={clubId} isAdmin={isAdmin} canStart={canStart} meId={meId} current={current} onChange={refresh} onClose={() => setSearch({ mine: mineView })} />}
    </main>
  );
}
